import { formatPhoneNumber as phoneNumber } from "../lib/phone-format";
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

/**
 * The shared controls.
 *
 * Every screen used to declare its own `fieldClass` inline and lean on the
 * browser's own widgets, which is why the workspace looked generic no matter
 * how often a single screen was redesigned. Two rules here:
 *
 *   1. A field always carries a visible label. Placeholder-as-label vanishes
 *      the moment someone types, and a half-filled form becomes a column of
 *      unlabelled boxes.
 *   2. No native <select> and no datetime-local. Those render in the OS's own
 *      chrome — the grey arrow, the mm/dd/yyyy stub — and no class changes it.
 */

export const focusRing = "outline-none transition focus:border-[var(--accent)] focus:ring-2 focus:ring-[var(--accent-wash)]";
export const controlClass =
  `min-h-11 w-full rounded-xl border border-[var(--line)] bg-[var(--paper)] px-3.5 py-2 text-sm text-[var(--ink)] shadow-[0_1px_0_rgba(15,23,42,0.02)] placeholder:text-[var(--ink-faint)] ${focusRing}`;

/*
 * THE EYEBROW, AND WHAT ITS COLOUR MEANS. Grey on grey said nothing, so the
 * label over a section now carries the temperature of what is under it:
 *
 *   standard   blue    — this is a section, read it when you need it
 *   important  amber   — somebody has to decide something, or it lapses
 *   urgent     red     — money is owed now, or it is already late
 *   good       green   — current and healthy
 *   renewal    purple  — ready to renew
 */
export type LabelTone = "standard" | "important" | "urgent" | "good" | "renewal";

export const labelTone: Record<LabelTone, string> = {
  standard: "text-[var(--accent)]",
  important: "text-[var(--warn)]",
  urgent: "text-[var(--st-conflict)]",
  good: "text-[var(--good)]",
  renewal: "text-[var(--band-purple)]",
};

export function SectionLabel({ children, tone = "standard" }: { children: ReactNode; tone?: LabelTone }) {
  return (
    <span className={`text-[11px] font-semibold uppercase tracking-[.08em] ${labelTone[tone]}`}>
      {children}
    </span>
  );
}

export function Field({
  label,
  hint,
  error,
  required,
  children,
  htmlFor,
  labelId,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  children: ReactNode;
  htmlFor?: string;
  labelId?: string;
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-baseline gap-1.5">
        <label
          id={labelId}
          htmlFor={htmlFor}
          className={`text-[11px] font-semibold uppercase tracking-[.08em] text-[var(--ink-soft)]${
            // the required marker is drawn in CSS and the hint sits outside the
            // label, so a field's accessible name is exactly its label — the
            // thing anyone, screen reader or test, would call it
            required ? " after:ml-0.5 after:font-semibold after:text-[var(--ink)] after:content-['*']" : ""
          }`}
        >
          {label}
        </label>
        {hint && <span className="text-[11px] text-[var(--ink-faint)]">{hint}</span>}
      </div>
      {children}
      {error && (
        <p role="alert" className="text-[12px] font-semibold text-[var(--ink)] underline decoration-[var(--ink)] underline-offset-2">
          {error}
        </p>
      )}
    </div>
  );
}

export function TextInput({
  label,
  hint,
  error,
  required,
  ...props
}: { label: string; hint?: string; error?: string } & React.InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={id}>
      <input id={id} required={required} className={controlClass} {...props} />
    </Field>
  );
}

export function TextArea({
  label,
  hint,
  error,
  required,
  ...props
}: { label: string; hint?: string; error?: string } & React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const id = useId();
  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={id}>
      <textarea id={id} required={required} className={controlClass} {...props} />
    </Field>
  );
}

export type Option = { value: string; label: string; note?: string; disabled?: boolean };

/**
 * A real listbox, not a native <select>. The closed control and the open menu
 * are both ours, so a technician row can carry the trade underneath the name
 * instead of being crammed into one truncated line.
 */
export function Select({
  label,
  hint,
  error,
  required,
  name,
  value,
  options,
  placeholder = "Choose one",
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  error?: string;
  required?: boolean;
  name?: string;
  value: string;
  options: Option[];
  placeholder?: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLDivElement>(null);
  const id = useId();
  const chosen = options.find((option) => option.value === value) ?? null;

  useEffect(() => {
    if (!open) return;
    const away = (event: globalThis.MouseEvent) => {
      if (!wrap.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const pick = (option: Option) => {
    if (option.disabled) return;
    onChange(option.value);
    setOpen(false);
  };

  return (
    <Field label={label} hint={hint} error={error} required={required} htmlFor={id} labelId={`${id}-label`}>
      <div ref={wrap} className="relative">
        {name && <input type="hidden" name={name} value={value} />}
        <button
          id={id}
          type="button"
          disabled={disabled}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-labelledby={`${id}-label ${id}-value`}
          aria-required={required || undefined}
          onClick={() => setOpen((current) => !current)}
          onKeyDown={(event) => {
            if (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              setOpen(true);
              setActive(Math.max(0, options.findIndex((option) => option.value === value)));
            }
          }}
          className={`flex items-center justify-between gap-2 text-left ${controlClass} ${
            disabled ? "cursor-not-allowed bg-[var(--wash)] text-[var(--ink-faint)]" : ""
          }`}
        >
          <span id={`${id}-value`} className={chosen ? "truncate text-[var(--ink)]" : "truncate text-[var(--ink-faint)]"}>
            {chosen ? chosen.label : placeholder}
          </span>
          <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden className="shrink-0 text-[var(--ink-soft)]">
            <path d="M2 4.5 6 8.5 10 4.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </button>
        {open && (
          <ul
            role="listbox"
            tabIndex={-1}
            onKeyDown={(event) => {
              if (event.key === "Escape") setOpen(false);
              if (event.key === "ArrowDown") { event.preventDefault(); setActive((n) => Math.min(options.length - 1, n + 1)); }
              if (event.key === "ArrowUp") { event.preventDefault(); setActive((n) => Math.max(0, n - 1)); }
              if (event.key === "Enter") { event.preventDefault(); const option = options[active]; if (option) pick(option); }
            }}
            className="absolute z-30 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-[var(--line-strong)] bg-[var(--paper)] py-1"
          >
            {options.map((option, index) => (
              <li key={option.value} role="option" aria-selected={option.value === value}>
                <button
                  type="button"
                  disabled={option.disabled}
                  onMouseEnter={() => setActive(index)}
                  onClick={() => pick(option)}
                  className={`flex w-full items-start gap-2 px-3 py-2 text-left text-sm ${
                    option.disabled
                      ? "cursor-not-allowed text-[var(--ink-faint)]"
                      : index === active
                        ? "bg-[var(--wash)] text-[var(--ink)]"
                        : "text-[var(--ink)]"
                  }`}
                >
                  <span className="min-w-0 flex-1">
                    <span className="block truncate font-semibold">{option.label}</span>
                    {option.note && <span className="mt-0.5 block truncate text-[12px] text-[var(--ink-soft)]">{option.note}</span>}
                  </span>
                  {option.value === value && (
                    <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden className="mt-0.5 shrink-0 text-[var(--accent)]">
                      <path d="M2.5 7.5 5.5 10.5 11.5 3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </button>
              </li>
            ))}
            {!options.length && <li className="px-3 py-6 text-center text-sm text-[var(--ink-faint)]">Nothing to choose from.</li>}
          </ul>
        )}
      </div>
    </Field>
  );
}

export function Segment({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-md border px-2.5 py-1 text-[11.5px] font-medium transition ${
        active
          ? "border-[var(--accent)] bg-[var(--accent-wash)] text-[var(--accent)]"
          : "border-[var(--line)] bg-[var(--paper)] text-[var(--ink-soft)] hover:border-[var(--line-strong)] hover:text-[var(--ink)]"
      }`}
    >
      {children}
    </button>
  );
}

export function Checkbox({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  children: ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-[13px] text-[var(--ink)]">
      <span
        className={`grid h-4 w-4 shrink-0 place-items-center rounded-xl border transition ${
          checked ? "border-[var(--ink)] bg-[var(--ink)]" : "border-[var(--line)] bg-[var(--paper)]"
        }`}
      >
        {checked && (
          <svg width="10" height="10" viewBox="0 0 14 14" aria-hidden className="text-[var(--paper)]">
            <path d="M2.5 7.5 5.5 10.5 11.5 3.5" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} className="sr-only" />
      {children}
    </label>
  );
}

/** "14:30" → "2:30 PM". Local wall-clock only; no Date, no timezone guessing. */
export function readableTime(value: string) {
  const [rawHour, minute] = value.split(":");
  const hour = Number(rawHour);
  if (!Number.isFinite(hour)) return value;
  const suffix = hour >= 12 ? "PM" : "AM";
  const twelve = hour % 12 === 0 ? 12 : hour % 12;
  return `${twelve}:${minute} ${suffix}`;
}

export function readableLength(minutes: number) {
  if (minutes % 60 === 0) return `${minutes / 60} hr`;
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} hr ${minutes % 60} min`;
}

export function endTimeForRange(start: string, minutes: number): string | null {
  const endMinutes = Number(start.slice(0, 2)) * 60 + Number(start.slice(3, 5)) + minutes;
  if (!Number.isFinite(endMinutes) || endMinutes >= 24 * 60) return null;
  return `${String(Math.floor(endMinutes / 60)).padStart(2, "0")}:${String(endMinutes % 60).padStart(2, "0")}`;
}

const DURATIONS = [30, 60, 90, 120, 240, 480];

/**
 * A start time and how long it takes — never two datetime-local stubs. The day
 * is already decided by the board, so the control does not ask for it twice.
 */
export function TimeRange({
  label,
  start,
  minutes,
  onChange,
  startHour = 0,
  endHour = 23,
}: {
  label: string;
  start: string;
  minutes: number;
  onChange: (start: string, minutes: number) => void;
  startHour?: number;
  endHour?: number;
}) {
  const end = endTimeForRange(start, minutes);
  return (
    <div className="space-y-2">
      <Field label={label} htmlFor={`${label.replaceAll(" ", "-").toLowerCase()}-time`}>
        <input id={`${label.replaceAll(" ", "-").toLowerCase()}-time`} aria-label={`${label} time`} type="time" value={start} min={`${String(startHour).padStart(2, "0")}:00`} max={`${String(endHour).padStart(2, "0")}:00`} step="60" onChange={(event) => onChange(event.target.value, minutes)} className={controlClass} />
      </Field>
      <div className="flex flex-wrap gap-1.5">
        {DURATIONS.map((option) => (
          <Segment key={option} active={option === minutes} onClick={() => { if (endTimeForRange(start, option)) onChange(start, option); }}>
            {readableLength(option)}
          </Segment>
        ))}
      </div>
      <p className="text-[12px] font-semibold text-[var(--ink-soft)]">
        {end ? `${readableTime(start)}–${readableTime(end)} · ${readableLength(minutes)}` : "Choose a duration that ends before midnight."}
      </p>
    </div>
  );
}

export function Dialog({
  title,
  className = "",
  overlayClassName = "",
  modal = true,
  header,
  subtitle,
  footer,
  onClose,
  children,
  width = "max-w-lg",
}: {
  title: string;
  className?: string;
  overlayClassName?: string;
  modal?: boolean;
  header?: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  onClose: () => void;
  children: ReactNode;
  width?: string;
}) {
  useEffect(() => {
    const key = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose]);
  return (
    <div className={`fixed inset-0 z-50 grid place-items-center p-3 sm:p-6 ${!modal ? "pointer-events-none" : ""} ${overlayClassName}`}>
      {modal && <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-slate-950/35 backdrop-blur-sm" />}
      <div
        role="dialog"
        aria-modal={modal}
        aria-label={title}
        className={`relative pointer-events-auto flex max-h-[88vh] w-full ${width} ${className} flex-col overflow-hidden rounded-2xl border border-[var(--line)] bg-[var(--paper)] shadow-[0_28px_90px_rgba(15,23,42,0.24)]`}
      >
        {header ?? <header className="flex items-center justify-between gap-3 border-b border-[var(--line)] bg-[var(--paper)] px-5 py-4 sm:px-6">
          <div>
            <h2 className="text-xl font-semibold tracking-tight text-[var(--ink)]">{title}</h2>
            {subtitle && <div className="dialog-subtitle">{subtitle}</div>}
          </div>
          <CloseButton onClose={onClose} />
        </header>}
        <div className="dialog-body min-h-0 overflow-y-auto px-5 py-5 sm:px-6">{children}</div>
        {footer && <footer className="dialog-footer shrink-0 border-t border-[var(--line)] px-5 py-4 sm:px-6">{footer}</footer>}
      </div>
    </div>
  );
}

export function Flyout({
  className,
  centered = false,
  eyebrow,
  title,
  onClose,
  children,
  footer,
}: {
  className?: string;
  centered?: boolean;
  eyebrow?: string;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  useEffect(() => {
    const key = (event: KeyboardEvent) => event.key === "Escape" && onClose();
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose]);
  return (
    <div className={`fixed inset-0 z-50 flex ${centered ? "items-center justify-center p-3 sm:p-6" : "justify-end"}`}>
      <button type="button" aria-label="Close" onClick={onClose} className="absolute inset-0 bg-[var(--ink)]/25" />
      <aside role="dialog" aria-modal="true" className={`relative flex ${centered ? "max-h-[90vh] w-full max-w-6xl rounded-2xl shadow-2xl" : "h-full w-full max-w-md"} flex-col border border-[var(--line-strong)] bg-[var(--paper)] ${className ?? ""}`}>
        <header className="flex items-start justify-between gap-3 border-b border-[var(--line)] px-5 py-4">
          <div className="min-w-0">
            {eyebrow && (
              <p className="text-[11px] font-semibold uppercase tracking-[.1em] text-[var(--ink-soft)]">{eyebrow}</p>
            )}
            <div className="mt-0.5 text-lg font-semibold text-[var(--ink)]">{title}</div>
          </div>
          <CloseButton onClose={onClose} />
        </header>
        <div className="flex-1 overflow-y-auto px-5 py-4">{children}</div>
        {footer && <div className="border-t border-[var(--line)] px-5 py-4">{footer}</div>}
      </aside>
    </div>
  );
}

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="grid h-7 w-7 shrink-0 place-items-center rounded-lg text-[var(--ink-soft)] hover:bg-[var(--wash)] hover:text-[var(--ink)]"
    >
      <svg width="12" height="12" viewBox="0 0 12 12" aria-hidden>
        <path d="M2 2 10 10M10 2 2 10" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      </svg>
    </button>
  );
}

export function PrimaryButton({
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={`min-h-11 w-full rounded-xl bg-[var(--accent)] px-4 text-sm font-semibold text-white shadow-[0_12px_28px_rgba(6,27,58,0.18)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:bg-[var(--wash)] disabled:text-[var(--ink-faint)] disabled:shadow-none ${props.className ?? ""}`}
    >
      {children}
    </button>
  );
}

export function QuietButton({
  children,
  ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return (
    <button
      {...props}
      className={`min-h-10 rounded-xl border border-[var(--line)] bg-[var(--paper)] px-3 text-[13px] font-semibold text-[var(--ink)] shadow-[0_1px_0_rgba(15,23,42,0.02)] transition hover:border-[var(--line-strong)] hover:bg-[var(--wash)] disabled:opacity-40 ${props.className ?? ""}`}
    >
      {children}
    </button>
  );
}

/**
 * A phone number the way the office writes one. Ten digits become
 * (505)206-5044, eleven with a leading 1 lose it, and anything else — an
 * extension, an international number, a half-typed field — is left exactly as
 * it was rather than mangled into a shape it does not have.
 */
export { phoneNumber };

/** One person we might ring, with the channel spelled out. */
export function ContactLine({ channel, value, note }: { channel: "phone" | "email"; value: string; note?: string }) {
  return (
    <a
      href={channel === "phone" ? `tel:${value}` : `mailto:${value}`}
      className="flex items-center gap-2 text-[13px] text-[var(--ink)] hover:text-[var(--accent)]"
    >
      <span className="grid h-5 w-5 shrink-0 place-items-center rounded-lg border border-[var(--line)] text-[var(--ink-soft)]" aria-hidden>
        {channel === "phone" ? (
          <svg width="11" height="11" viewBox="0 0 14 14"><path d="M3 2h2.5l1 3-1.7 1.2a9 9 0 0 0 3.9 3.9L10 8.5l3 1V12a1 1 0 0 1-1.1 1A11 11 0 0 1 2 3.1 1 1 0 0 1 3 2Z" fill="currentColor" /></svg>
        ) : (
          <svg width="11" height="11" viewBox="0 0 14 14"><path d="M1.5 3.5h11v7h-11z" fill="none" stroke="currentColor" strokeWidth="1.4" /><path d="m1.5 4 5.5 3.5L12.5 4" fill="none" stroke="currentColor" strokeWidth="1.4" /></svg>
        )}
      </span>
      <span className="truncate font-semibold">{channel === "phone" ? phoneNumber(value) : value}</span>
      {note && <span className="shrink-0 rounded-lg border border-[var(--line)] px-1.5 py-0.5 text-[11px] font-semibold text-[var(--ink-soft)]">{note}</span>}
    </a>
  );
}
